import React, { useEffect, useRef, useState } from 'react';
import './ContextMenu.css';

const ContextMenu = ({ 
  x, 
  y, 
  isVisible, 
  onClose, 
  options = [] 
}) => {
  const menuRef = useRef(null);
  const [adjustedPosition, setAdjustedPosition] = useState({ x, y });

  // Reset position when menu becomes visible or coordinates change
  useEffect(() => {
    if (isVisible) {
      setAdjustedPosition({ x, y });
    }
  }, [isVisible, x, y]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        onClose();
      }
    };

    const handleEscapeKey = (event) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    if (isVisible) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleEscapeKey);
      
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
        document.removeEventListener('keydown', handleEscapeKey);
      };
    }
  }, [isVisible, onClose]);

  // Adjust position after the menu is rendered
  useEffect(() => {
    if (isVisible && menuRef.current) {
      const adjustPosition = () => {
        const rect = menuRef.current.getBoundingClientRect();
        const viewportWidth = window.innerWidth;
        const viewportHeight = window.innerHeight;

        let adjustedX = x;
        let adjustedY = y;

        console.log('🎯 Position adjustment (fixed positioning):', {
          originalX: x,
          originalY: y,
          menuWidth: rect.width,
          menuHeight: rect.height,
          viewportWidth,
          viewportHeight
        });

        // Calculate available space in viewport (no scroll offsets needed for fixed positioning)
        const spaceRight = viewportWidth - x;
        const spaceLeft = x;
        const spaceBelow = viewportHeight - y;
        const spaceAbove = y;

        // Adjust horizontal position
        if (rect.width > spaceRight && spaceLeft > spaceRight) {
          // Show menu to the left of cursor
          adjustedX = Math.max(10, x - rect.width);
          console.log('🔄 Adjusting X position (left):', adjustedX);
        } else if (rect.width > spaceRight) {
          // Keep menu within right boundary
          adjustedX = Math.max(10, viewportWidth - rect.width - 10);
          console.log('🔄 Adjusting X position (right boundary):', adjustedX);
        }

        // Adjust vertical position
        if (rect.height > spaceBelow && spaceAbove > spaceBelow) {
          // Show menu above cursor
          adjustedY = Math.max(10, y - rect.height);
          console.log('🔄 Adjusting Y position (above):', adjustedY);
        } else if (rect.height > spaceBelow) {
          // Keep menu within bottom boundary
          adjustedY = Math.max(10, viewportHeight - rect.height - 10);
          console.log('🔄 Adjusting Y position (bottom boundary):', adjustedY);
        }

        // Final boundary checks (viewport relative)
        if (adjustedX < 10) {
          adjustedX = 10;
        }
        if (adjustedY < 10) {
          adjustedY = 10;
        }
        if (adjustedX + rect.width > viewportWidth - 10) {
          adjustedX = viewportWidth - rect.width - 10;
        }
        if (adjustedY + rect.height > viewportHeight - 10) {
          adjustedY = viewportHeight - rect.height - 10;
        }

        setAdjustedPosition({ x: adjustedX, y: adjustedY });
      };

      // Use requestAnimationFrame to ensure DOM is fully rendered
      requestAnimationFrame(adjustPosition);
    }
  }, [isVisible, x, y, options.length]);

  const handleOptionClick = (option) => {
    if (option.onClick) {
      option.onClick();
    }
    onClose();
  };

  if (!isVisible) return null;
  
  console.log('🎯 ContextMenu rendering:', { 
    isVisible, 
    originalX: x, 
    originalY: y, 
    adjustedPosition, 
    optionsLength: options.length 
  });

  return (
    <div 
      ref={menuRef}
      className="context-menu"
      style={{
        left: adjustedPosition.x,
        top: adjustedPosition.y,
      }}
    >
      {options.map((option, index) => (
        <div key={index}>
          {/* Handle different item types */}
          {option.type === 'header' ? (
            <div className="context-menu-header">
              {option.icon && <span className="context-menu-header-icon">{option.icon}</span>}
              <span className="context-menu-header-label">{option.label}</span>
            </div>
          ) : option.type === 'divider' || option.separator ? (
            <div className="context-menu-separator" />
          ) : (
            <button
              className={`context-menu-item ${option.disabled ? 'disabled' : ''}`}
              onClick={() => !option.disabled && handleOptionClick(option)}
              disabled={option.disabled}
            >
              {option.icon && <span className="context-menu-icon">{option.icon}</span>}
              <span className="context-menu-label">{option.label}</span>
              {option.shortcut && <span className="context-menu-shortcut">{option.shortcut}</span>}
            </button>
          )}
        </div>
      ))}
    </div>
  );
};

export default ContextMenu; 